import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Building2, Heart, Landmark, Shield, TrendingUp, Home, Layers, Package, TableProperties, Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Mail, Phone, MapPin, Globe, Users, Calculator } from "lucide-react";
import { toast } from "sonner";

// Visual identity per company category — instantly distinguishes Krankenkasse,
// Vorsorge (life/pension) and Sachversicherung (Protezione) in the sidebar.
const categoryIcon = (cat) => {
  const c = (cat || "").toLowerCase();
  if (c === "krankenversicherung") return Heart;       // Kranken → cuore
  if (c === "sachversicherung") return Shield;         // Protezione → scudo
  if (c === "lebensversicherung") return Landmark;     // Vorsorge → casa con colonne
  if (c === "investment") return TrendingUp;
  if (c === "hypothek") return Home;
  if (c === "mixed") return Layers;
  return Building2;
};
const categoryColor = (cat, sel) => {
  const c = (cat || "").toLowerCase();
  if (sel) return "text-prime";
  if (c === "krankenversicherung") return "text-rose-500";
  if (c === "sachversicherung") return "text-sky-600";
  if (c === "lebensversicherung") return "text-emerald-600";
  if (c === "investment") return "text-violet-600";
  if (c === "hypothek") return "text-amber-600";
  if (c === "mixed") return "text-slate-500";
  return "text-muted-foreground";
};

const CALC_TYPES = [
  { value: "premio_annuo", label: "% des jährlichen Prämiens" },
  { value: "premio_x_anni", label: "% × Jahre × Jahresprämie" },
  { value: "capitale_investito", label: "% des Anlagekapitals" },
  { value: "chf_fisso", label: "Fester CHF-Betrag" },
  { value: "componenti", label: "Komponenten — Prämie × 12 × MIN(Jahre, Max) × Rate × Split" },
  { value: "kvg_vvg", label: "Krankenkasse — KVG + VVG (Prämie × 12)" },
];

const slugKey = (name) => (name || "").toLowerCase()
  .replaceAll("ä", "ae").replaceAll("ö", "oe").replaceAll("ü", "ue").replaceAll("ß", "ss")
  .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

function CompanyDialog({ company, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    name: "", code: "", category: "lebensversicherung", status: "active",
    storno_default_months: 24, payment_mode: "monthly",
    website: "", email: "", phone: "", address: "", postal_code: "", city: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: company?.name || "", code: company?.code || "",
        category: (company?.category || "lebensversicherung").toLowerCase(),
        status: company?.status || "active",
        storno_default_months: company?.storno_default_months || 24,
        payment_mode: company?.payment_mode || "monthly",
        website: company?.website || "", email: company?.email || "", phone: company?.phone || "",
        address: company?.address || "", postal_code: company?.postal_code || "", city: company?.city || "",
      });
    }
  }, [open, company]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    setBusy(true);
    try {
      const r = company?.id
        ? await api.patch(`/companies/${company.id}`, form)
        : await api.post("/companies", form);
      toast.success(company?.id ? "Aktualisiert" : "Gesellschaft erstellt");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{company?.id ? "Gesellschaft bearbeiten" : "Neue Gesellschaft"}</DialogTitle>
          <DialogDescription>Stammdaten + Provisionsregelung der Gesellschaft.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="label-uppercase text-muted-foreground">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10" data-testid="company-name" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Code</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5 h-10" placeholder="z.B. PAX001" data-testid="company-code" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Kategorie</Label>
            <Select
              value={(form.category || "").toLowerCase()}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger className="h-10 mt-1.5" data-testid="company-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lebensversicherung">Lebensversicherung (Vorsorge)</SelectItem>
                <SelectItem value="krankenversicherung">Krankenversicherung (Kranken)</SelectItem>
                <SelectItem value="sachversicherung">Sachversicherung (Protezione)</SelectItem>
                <SelectItem value="investment">Investment</SelectItem>
                <SelectItem value="hypothek">Hypothek</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Storno (Monate)</Label>
            <Input type="number" value={form.storno_default_months} onChange={(e) => setForm({ ...form, storno_default_months: parseInt(e.target.value) || 0 })} className="mt-1.5 h-10" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Auszahlung</Label>
            <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monatlich</SelectItem>
                <SelectItem value="advance">Vorschuss</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="label-uppercase text-muted-foreground">Webseite</Label>
            <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1.5 h-10" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">E-Mail</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-10" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Telefon</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-10" data-testid="company-phone" />
          </div>
          <div className="col-span-2">
            <Label className="label-uppercase text-muted-foreground">Adresse</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 h-10" placeholder="Strasse Nr." data-testid="company-address" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">PLZ</Label>
            <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1.5 h-10" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Ort</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="btn-prime" onClick={save} disabled={busy} data-testid="save-company">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComponentsEditor({ rate, components, onRateChange, onChange }) {
  const update = (i, patch) => {
    const next = components.map((c, idx) => {
      if (idx !== i) return c;
      const merged = { ...c, ...patch };
      if (patch.name !== undefined) merged.key = slugKey(patch.name);
      return merged;
    });
    onChange(next);
  };
  return (
    <div className="space-y-2 border border-border rounded-md p-3 bg-secondary/30" data-testid="components-editor">
      <div className="flex items-center gap-2">
        <Calculator className="w-3.5 h-3.5 text-prime" strokeWidth={1.7} />
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Provisions-Komponenten</span>
      </div>
      <div>
        <Label className="label-uppercase text-muted-foreground">Tasso / Rate</Label>
        <Input type="number" step="0.001" value={rate}
          onChange={(e) => onRateChange(parseFloat(e.target.value) || 0)}
          className="mt-1.5 h-9 w-32" placeholder="0.046" data-testid="product-rate" />
      </div>
      {components.map((c, i) => (
        <div key={i} className="grid grid-cols-[1.8fr_0.7fr_0.7fr_auto] gap-2 items-end" data-testid={`component-row-${i}`}>
          <div>
            {i === 0 && <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Komponente</Label>}
            <Input value={c.name} onChange={(e) => update(i, { name: e.target.value })}
              className="h-9 mt-1 text-xs" placeholder="z.B. Ausbaugarantie" data-testid={`component-name-${i}`} />
          </div>
          <div>
            {i === 0 && <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Max Jahre</Label>}
            <Input type="number" value={c.max_years} onChange={(e) => update(i, { max_years: parseInt(e.target.value) || 0 })}
              className="h-9 mt-1 text-xs" data-testid={`component-max-${i}`} />
          </div>
          <div>
            {i === 0 && <Label className="text-[9px] uppercase tracking-widest text-muted-foreground">Split %</Label>}
            <Input type="number" value={c.split_percent} onChange={(e) => update(i, { split_percent: parseFloat(e.target.value) || 0 })}
              className="h-9 mt-1 text-xs" data-testid={`component-split-${i}`} />
          </div>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50 mb-0.5"
            onClick={() => onChange(components.filter((_, idx) => idx !== i))} data-testid={`component-del-${i}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1.5"
        onClick={() => onChange([...components, { key: "", name: "", max_years: 99, split_percent: 100 }])}
        data-testid="component-add">
        <Plus className="w-3.5 h-3.5" /> Komponente
      </Button>
      <div className="text-[10px] text-muted-foreground font-mono">
        Formel: Monatsprämie × 12 × MIN(Dauer, Max Jahre) × Rate × Split
      </div>
    </div>
  );
}

function ProductDialog({ product, companyId, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    name: "", code: "", category: "Lebensversicherung",
    commission_type: "premio_annuo", commission_percent: 0, storno_haftung_months: 24,
    description: "", commission_rate: 0, commission_components: [],
    kvg_calc_mode: "flat",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: product?.name || "", code: product?.code || "",
        category: product?.category || "Lebensversicherung",
        commission_type: product?.commission_type || "premio_annuo",
        commission_percent: product?.commission_percent || 0,
        storno_haftung_months: product?.storno_haftung_months || 24,
        description: product?.description || "",
        commission_rate: product?.commission_rate || 0,
        commission_components: product?.commission_components || [],
        kvg_calc_mode: product?.kvg_calc_mode || "flat",
      });
    }
  }, [open, product]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    setBusy(true);
    try {
      const payload = { ...form, company_id: companyId };
      const r = product?.id
        ? await api.patch(`/providers/products/${product.id}`, form)
        : await api.post("/providers/products", payload);
      toast.success(product?.id ? "Produkt aktualisiert" : "Produkt erstellt");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{product?.id ? "Produkt bearbeiten" : "Neues Produkt"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Produktname</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10" data-testid="product-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase text-muted-foreground">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5 h-10" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">Storno (Monate)</Label>
              <Input type="number" value={form.storno_haftung_months} onChange={(e) => setForm({ ...form, storno_haftung_months: parseInt(e.target.value) || 0 })} className="mt-1.5 h-10" />
            </div>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Berechnungsart (Default)</Label>
            <Select value={form.commission_type} onValueChange={(v) => setForm({ ...form, commission_type: v })}>
              <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CALC_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.commission_type === "componenti" ? (
            <ComponentsEditor
              rate={form.commission_rate}
              components={form.commission_components}
              onRateChange={(r) => setForm({ ...form, commission_rate: r })}
              onChange={(comps) => setForm({ ...form, commission_components: comps })}
            />
          ) : form.commission_type === "kvg_vvg" ? (
            <div className="space-y-2 rounded-md border border-prime/20 bg-prime/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-prime font-semibold">KVG / VVG Logik</div>
              <div>
                <Label className="label-uppercase text-muted-foreground">KVG-Berechnung</Label>
                <Select
                  value={form.kvg_calc_mode || "flat"}
                  onValueChange={(v) => setForm({ ...form, kvg_calc_mode: v })}
                >
                  <SelectTrigger className="h-10 mt-1.5" data-testid="kvg-calc-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Pauschal CHF 70 (Standard)</SelectItem>
                    <SelectItem value="premium_x_12">Prämie KVG × 12 (wie VVG)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-muted-foreground mt-1 font-mono leading-relaxed">
                  · KVG flat = einmalig CHF 70 pro Police wenn KVG-Beginn ausgefüllt<br />
                  · KVG × 12 = Monatsprämie KVG × 12 (1 Jahr Gesamtprämie)<br />
                  · VVG = Monatsprämie VVG × 12 (immer, unabhängig vom KVG-Modus)
                </div>
              </div>
            </div>
          ) : (
            <div>
              <Label className="label-uppercase text-muted-foreground">Basis-% / Betrag</Label>
              <Input type="number" step="0.01" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: parseFloat(e.target.value) || 0 })} className="mt-1.5 h-10" />
              <div className="text-[10px] text-muted-foreground mt-1">Fallback wenn keine Jahres-Regel zutrifft</div>
            </div>
          )}
          <div>
            <Label className="label-uppercase text-muted-foreground">Beschreibung</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 h-10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="btn-prime" onClick={save} disabled={busy} data-testid="save-product">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RuleRow({ rule, onEdit, onDelete }) {
  const calcLabel = CALC_TYPES.find((c) => c.value === rule.calc_type)?.label || rule.calc_type;
  return (
    <tr className="border-b border-border hover:bg-secondary group">
      <td className="py-2 px-3 font-mono text-xs">{rule.years_from} – {rule.years_to}</td>
      <td className="py-2 px-3 font-bold text-prime">{rule.percentage}{rule.calc_type === "chf_fisso" ? " CHF" : "%"}</td>
      <td className="py-2 px-3 text-xs text-muted-foreground">{calcLabel}</td>
      <td className="py-2 px-3 text-xs">{rule.notes || ""}</td>
      <td className="py-2 px-3 w-16 text-right">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(rule)} data-testid={`edit-rule-${rule.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50"
          onClick={() => { if (window.confirm("Regel löschen?")) onDelete(rule.id); }}
          data-testid={`del-rule-${rule.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </td>
    </tr>
  );
}

function RuleDialog({ rule, productId, defaultCalc, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    years_from: 1, years_to: 99, percentage: 0, calc_type: "premio_annuo", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        years_from: rule?.years_from ?? 1, years_to: rule?.years_to ?? 99,
        percentage: rule?.percentage ?? 0,
        calc_type: rule?.calc_type || defaultCalc || "premio_annuo",
        notes: rule?.notes || "",
      });
    }
  }, [open, rule, defaultCalc]);

  const save = async () => {
    if (form.years_from > form.years_to) { toast.error("Jahre 'von' muss ≤ 'bis' sein"); return; }
    setBusy(true);
    try {
      const r = rule?.id
        ? await api.patch(`/commission-rules/${rule.id}`, form)
        : await api.post("/commission-rules", { ...form, product_id: productId });
      toast.success(rule?.id ? "Regel aktualisiert" : "Regel erstellt");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{rule?.id ? "Regel bearbeiten" : "Neue Provisionsregel"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Von Jahr</Label>
            <Input type="number" value={form.years_from} onChange={(e) => setForm({ ...form, years_from: parseInt(e.target.value) || 1 })} className="mt-1.5 h-10" data-testid="rule-yfrom" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Bis Jahr</Label>
            <Input type="number" value={form.years_to} onChange={(e) => setForm({ ...form, years_to: parseInt(e.target.value) || 99 })} className="mt-1.5 h-10" data-testid="rule-yto" />
          </div>
          <div className="col-span-2">
            <Label className="label-uppercase text-muted-foreground">Berechnungsart</Label>
            <Select value={form.calc_type} onValueChange={(v) => setForm({ ...form, calc_type: v })}>
              <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CALC_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="label-uppercase text-muted-foreground">{form.calc_type === "chf_fisso" ? "CHF" : "Prozent"}</Label>
            <Input type="number" step="0.01" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: parseFloat(e.target.value) || 0 })} className="mt-1.5 h-10" data-testid="rule-pct" />
          </div>
          <div className="col-span-2">
            <Label className="label-uppercase text-muted-foreground">Notiz</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 h-10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="btn-prime" onClick={save} disabled={busy} data-testid="save-rule">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductCard({ product, onProductChanged, onProductDeleted }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [openProduct, setOpenProduct] = useState(false);
  const [openRule, setOpenRule] = useState(null); // null=closed, {} = create, {rule}=edit

  const loadRules = async () => {
    try {
      const r = await api.get("/commission-rules", { params: { product_id: product.id } });
      setRules(r.data || []);
    } catch { /* */ }
  };
  useEffect(() => { if (open) loadRules(); }, [open, product.id]); // eslint-disable-line

  const deleteRule = async (id) => {
    try { await api.delete(`/commission-rules/${id}`); await loadRules(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="border border-border rounded-md overflow-hidden bg-card" data-testid={`product-card-${product.id}`}>
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary" onClick={() => setOpen(!open)}>
        <Package className="w-4 h-4 text-prime shrink-0" strokeWidth={1.7} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{product.name} <span className="font-mono text-xs text-muted-foreground">({product.code})</span></div>
          <div className="text-xs text-muted-foreground">
            {product.commission_type === "componenti"
              ? `Komponenten (${(product.commission_components || []).length}) · Rate ${product.commission_rate} · Storno ${product.storno_haftung_months} Mo`
              : product.commission_type === "kvg_vvg"
                ? `KVG ${product.kvg_calc_mode === "premium_x_12" ? "(Prämie × 12)" : "(CHF 70 flat)"} + VVG (Prämie × 12) · Storno ${product.storno_haftung_months} Mo`
                : `${CALC_TYPES.find(c => c.value === product.commission_type)?.label || product.commission_type} · Storno ${product.storno_haftung_months} Mo · Basis ${product.commission_percent}${product.commission_type === "chf_fisso" ? " CHF" : "%"}`}
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setOpenProduct(true); }} data-testid={`edit-product-${product.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50"
          onClick={(e) => { e.stopPropagation(); if (window.confirm("Produkt löschen?")) onProductDeleted(product.id); }}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
        {open ? <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-90" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </div>
      {open && product.commission_type === "componenti" && (
        <div className="border-t border-border bg-card/50" data-testid={`components-table-${product.id}`}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Calculator className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.7} />
            <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Komponenten ({(product.commission_components || []).length}) · Rate {product.commission_rate}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="text-left py-1.5 px-3 font-bold">Komponente</th>
                <th className="text-left py-1.5 px-3 font-bold">Max Jahre</th>
                <th className="text-left py-1.5 px-3 font-bold">Split</th>
                <th className="text-left py-1.5 px-3 font-bold hidden sm:table-cell">Formel</th>
              </tr>
            </thead>
            <tbody>
              {(product.commission_components || []).map((c) => (
                <tr key={c.key} className="border-b border-border">
                  <td className="py-2 px-3 font-medium text-xs">{c.name}</td>
                  <td className="py-2 px-3 font-mono text-xs">{c.max_years}</td>
                  <td className="py-2 px-3 font-bold text-prime text-xs">{c.split_percent}%</td>
                  <td className="py-2 px-3 font-mono text-[10px] text-muted-foreground hidden sm:table-cell">
                    Prämie/Mt × 12 × MIN(Jahre, {c.max_years}) × {product.commission_rate} × {(c.split_percent / 100).toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-[10px] font-mono text-muted-foreground bg-secondary/30">
            TOT = Σ (Monatsprämie × 12 × MIN(Dauer, Max Jahre) × {product.commission_rate} × Split)
          </div>
        </div>
      )}
      {open && product.commission_type !== "componenti" && (
        <div className="border-t border-border bg-card/50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <div className="flex items-center gap-2">
              <TableProperties className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.7} />
              <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Provisionsregeln ({rules.length})</span>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpenRule({})} data-testid={`new-rule-${product.id}`}>
              <Plus className="w-3 h-3 mr-1" /> Regel
            </Button>
          </div>
          {rules.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Keine Regeln definiert — Basis-% des Produkts wird verwendet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="text-left py-1.5 px-3 font-bold">Jahre</th>
                  <th className="text-left py-1.5 px-3 font-bold">Provision</th>
                  <th className="text-left py-1.5 px-3 font-bold">Berechnung</th>
                  <th className="text-left py-1.5 px-3 font-bold">Notiz</th>
                  <th className="w-16" />
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <RuleRow key={r.id} rule={r} onEdit={(rl) => setOpenRule(rl)} onDelete={deleteRule} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <ProductDialog product={product} companyId={product.company_id} open={openProduct}
        onOpenChange={setOpenProduct} onSaved={onProductChanged} />
      <RuleDialog rule={openRule?.id ? openRule : null} productId={product.id} defaultCalc={product.commission_type}
        open={!!openRule} onOpenChange={() => setOpenRule(null)} onSaved={() => loadRules()} />
    </div>
  );
}

function ContactDialog({ contact, companyId, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", role: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        first_name: contact?.first_name || "", last_name: contact?.last_name || "",
        role: contact?.role || "", email: contact?.email || "", phone: contact?.phone || "",
      });
    }
  }, [open, contact]);

  const save = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) { toast.error("Name erforderlich"); return; }
    setBusy(true);
    try {
      contact?.id
        ? await api.patch(`/providers/contacts/${contact.id}`, form)
        : await api.post("/providers/contacts", { ...form, company_id: companyId });
      toast.success(contact?.id ? "Kontakt aktualisiert" : "Kontakt erstellt");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{contact?.id ? "Kontakt bearbeiten" : "Neuer Ansprechpartner"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase text-muted-foreground">Vorname</Label>
            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5 h-10" data-testid="contact-first-name" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Nachname</Label>
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5 h-10" data-testid="contact-last-name" />
          </div>
          <div className="col-span-2">
            <Label className="label-uppercase text-muted-foreground">Funktion</Label>
            <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 h-10" placeholder="z.B. Broker Manager" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">E-Mail</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-10" data-testid="contact-email" />
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Telefon</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-10" data-testid="contact-phone" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="btn-prime" onClick={save} disabled={busy} data-testid="save-contact">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactsSection({ companyId }) {
  const [contacts, setContacts] = useState([]);
  const [openDialog, setOpenDialog] = useState(null); // null | {} | {contact}

  const load = async () => {
    try {
      const r = await api.get(`/providers/${companyId}/contacts`);
      setContacts(r.data || []);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, [companyId]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Kontakt löschen?")) return;
    try { await api.delete(`/providers/contacts/${id}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="prime-card overflow-hidden" data-testid="contacts-section">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-prime" strokeWidth={1.7} />
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Ansprechpartner ({contacts.length})</span>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setOpenDialog({})} data-testid="new-contact-btn">
          <Plus className="w-3 h-3" /> Kontakt
        </Button>
      </div>
      {contacts.length === 0 ? (
        <div className="p-4 text-center text-xs text-muted-foreground">Noch keine Kontakte hinterlegt.</div>
      ) : (
        <div className="divide-y divide-border">
          {contacts.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/40" data-testid={`contact-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{c.first_name} {c.last_name} {c.role && <span className="font-normal text-xs text-muted-foreground">· {c.role}</span>}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                  {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-prime hover:underline"><Mail className="w-3 h-3" />{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" />{c.phone}</a>}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpenDialog(c)} data-testid={`edit-contact-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => remove(c.id)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <ContactDialog contact={openDialog?.id ? openDialog : null} companyId={companyId}
        open={!!openDialog} onOpenChange={() => setOpenDialog(null)} onSaved={load} />
    </div>
  );
}

export default function ProviderManagementPage() {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [openCompany, setOpenCompany] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [openProduct, setOpenProduct] = useState(false);

  const loadCompanies = async () => {
    const r = await api.get("/companies");
    setCompanies(r.data || []);
  };
  const loadProducts = async (cid) => {
    if (!cid) { setProducts([]); return; }
    const r = await api.get(`/providers/${cid}/products`);
    setProducts(r.data || []);
  };

  useEffect(() => { loadCompanies(); }, []);
  useEffect(() => { if (selected) loadProducts(selected.id); }, [selected?.id]); // eslint-disable-line

  const deleteCompany = async (id) => {
    if (!window.confirm("Gesellschaft wirklich löschen? Alle Produkte und Regeln werden entfernt.")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Gelöscht");
      setSelected(null);
      await loadCompanies();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const deleteProduct = async (id) => {
    try { await api.delete(`/providers/products/${id}`); toast.success("Gelöscht"); await loadProducts(selected.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const filtered = companies.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="provider-mgmt-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl tracking-tight">Anbieter-Verwaltung</h1>
          <p className="text-sm text-muted-foreground">Gesellschaften · Produkte · Provisionsregeln</p>
        </div>
        <Button className="btn-prime gap-2" onClick={() => { setEditCompany(null); setOpenCompany(true); }} data-testid="new-company-btn">
          <Plus className="w-4 h-4" /> Neue Gesellschaft
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Companies list */}
        <div className="prime-card overflow-hidden">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Gesellschaft suchen…" className="h-9 pl-8 text-sm" data-testid="search-company" />
            </div>
          </div>
          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Keine Einträge.</div>
            ) : filtered.map((c) => {
              const sel = selected?.id === c.id;
              const Icon = categoryIcon(c.category);
              return (
                <button key={c.id} onClick={() => setSelected(c)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-2 transition-colors ${sel ? "bg-prime/10 border-l-4 border-l-prime" : "hover:bg-secondary border-l-4 border-l-transparent"}`}
                  data-testid={`company-${c.id}`}
                >
                  <Icon className={`w-4 h-4 ${categoryColor(c.category, sel)} shrink-0`} strokeWidth={1.7} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{c.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.code || "—"} · {c.category}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Details panel */}
        <div className="space-y-4">
          {!selected ? (
            <div className="prime-card p-12 text-center text-sm text-muted-foreground">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" strokeWidth={1.2} />
              Wählen Sie links eine Gesellschaft aus, um Produkte und Provisionsregeln zu verwalten.
            </div>
          ) : (
            <>
              <div className="prime-card p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {(() => {
                      const Icon = categoryIcon(selected.category);
                      return (
                        <div className={`shrink-0 mt-1 w-10 h-10 rounded-md flex items-center justify-center bg-prime/5 ${categoryColor(selected.category, false)}`}>
                          <Icon className="w-5 h-5" strokeWidth={1.8} />
                        </div>
                      );
                    })()}
                    <div className="min-w-0">
                      <div className="label-uppercase text-prime">Gesellschaft</div>
                      <div className="font-display font-extrabold text-xl mt-0.5">{selected.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Code: <span className="font-mono">{selected.code || "—"}</span> · Kategorie: {selected.category} · Storno-Default: {selected.storno_default_months || 24} Mo · Auszahlung: {selected.payment_mode === "advance" ? "Vorschuss" : "Monatlich"}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" data-testid="company-anagrafica">
                        {selected.email && (
                          <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1.5 text-prime hover:underline">
                            <Mail className="w-3 h-3" />{selected.email}
                          </a>
                        )}
                        {selected.phone && (
                          <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-1.5 hover:underline">
                            <Phone className="w-3 h-3" />{selected.phone}
                          </a>
                        )}
                        {(selected.address || selected.city) && (
                          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {[selected.address, [selected.postal_code, selected.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
                          </span>
                        )}
                        {selected.website && (
                          <a href={selected.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-muted-foreground hover:underline">
                            <Globe className="w-3 h-3" />{selected.website.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditCompany(selected); setOpenCompany(true); }} data-testid="edit-company">
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Bearbeiten
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => deleteCompany(selected.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>

              <ContactsSection companyId={selected.id} />

              <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground">Produkte ({products.length})</h2>
                <Button size="sm" className="btn-prime gap-2" onClick={() => setOpenProduct(true)} data-testid="new-product-btn">
                  <Plus className="w-4 h-4" /> Neues Produkt
                </Button>
              </div>

              {products.length === 0 ? (
                <div className="prime-card p-12 text-center text-sm text-muted-foreground">
                  <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" strokeWidth={1.2} />
                  Noch keine Produkte für diese Gesellschaft.
                </div>
              ) : (
                <div className="space-y-3">
                  {products.map((p) => (
                    <ProductCard key={p.id} product={p}
                      onProductChanged={() => loadProducts(selected.id)}
                      onProductDeleted={deleteProduct}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CompanyDialog company={editCompany} open={openCompany}
        onOpenChange={setOpenCompany}
        onSaved={(saved) => { loadCompanies(); if (saved?.id === selected?.id) setSelected(saved); }}
      />
      {selected && (
        <ProductDialog product={null} companyId={selected.id} open={openProduct}
          onOpenChange={setOpenProduct} onSaved={() => loadProducts(selected.id)}
        />
      )}
    </div>
  );
}
